import type { ReactNode } from 'react';
import { posicionDelPaso, type Paso } from '../asistente.js';
import { instanteLargo, nombreDeZona } from '../fechas.js';
import { textos } from '../textos.js';

/** Aviso con papel de `alert` cuando es un error, para que lo lea el lector de pantalla. */
export function Aviso({
  tipo,
  titulo,
  children,
}: {
  tipo: 'error' | 'atencion' | 'informacion';
  titulo?: string;
  children: ReactNode;
}) {
  return (
    <div className={`aviso ${tipo}`} role={tipo === 'error' ? 'alert' : 'status'}>
      {titulo !== undefined && <h3>{titulo}</h3>}
      <p>{children}</p>
    </div>
  );
}

export function Cargando({ que = textos.aplicacion.cargando }: { que?: string }) {
  return (
    <div className="esqueleto" role="status" aria-live="polite">
      <span className="visualmente-oculto">{que}</span>
      <span className="linea-esqueleto larga" aria-hidden="true" />
      <span className="linea-esqueleto media" aria-hidden="true" />
      <span className="bloque-esqueleto" aria-hidden="true" />
    </div>
  );
}

/** Estado vacío con una explicación más visible que una línea de texto suelta. */
export function EstadoVacio({
  titulo,
  detalle,
  children,
  compacto = false,
}: {
  titulo: string;
  detalle?: string;
  children?: ReactNode;
  compacto?: boolean;
}) {
  return (
    <div className={`estado-vacio${compacto ? ' compacto' : ''}`} role="status">
      <span className="icono-estado-vacio" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M7 3v3m10-3v3M4 9h16M6 5h12a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
          <path d="m9.5 14 1.7 1.7 3.6-3.7" />
        </svg>
      </span>
      <div>
        <strong>{titulo}</strong>
        {detalle !== undefined && <p>{detalle}</p>}
        {children}
      </div>
    </div>
  );
}

const ETIQUETAS: Record<Paso, string> = {
  entidad: textos.pasos.entidad,
  acceso: textos.pasos.acceso,
  servicio: textos.pasos.servicio,
  hora: textos.pasos.hora,
  confirmacion: textos.pasos.confirmacion,
};

/**
 * Barra de progreso.
 *
 * Muestra **solo los pasos que ese cliente va a ver** (§9): si no tiene que
 * elegir servicio, ese paso no aparece ni se cuenta.
 */
export function BarraDeProgreso({ visibles, actual }: { visibles: Paso[]; actual: Paso }) {
  const posicionActual = posicionDelPaso(actual, visibles);
  const porcentaje = Math.round((posicionActual / visibles.length) * 100);

  return (
    <nav className="navegacion-progreso" aria-label="Progreso de la reserva">
      <div className="progreso-cabecera" aria-hidden="true">
        <span>{textos.aplicacion.tuReserva}</span>
        <strong>
          Paso {posicionActual} de {visibles.length}
        </strong>
      </div>
      <ol className="progreso">
        {visibles.map((paso, indice) => {
          const numero = indice + 1;
          const clase =
            numero === posicionActual ? 'actual' : numero < posicionActual ? 'hecho' : '';
          return (
            <li
              key={paso}
              className={clase}
              aria-current={numero === posicionActual ? 'step' : undefined}
            >
              <span className="progreso-nodo" aria-hidden="true">
                {numero < posicionActual ? '✓' : numero}
              </span>
              <span className="progreso-etiqueta">{ETIQUETAS[paso]}</span>
            </li>
          );
        })}
      </ol>
      <div className="progreso-movil" aria-hidden="true">
        <div className="progreso-movil-texto">
          <span>
            {textos.aplicacion.paso} {posicionActual} {textos.aplicacion.de} {visibles.length}
          </span>
          <strong>{ETIQUETAS[actual]}</strong>
        </div>
        <span className="progreso-movil-pista">
          <span style={{ width: `${porcentaje}%` }} />
        </span>
      </div>
      <p className="visualmente-oculto" aria-live="polite">
        Paso {posicionActual} de {visibles.length}: {ETIQUETAS[actual]}
      </p>
    </nav>
  );
}

/** Contexto que acompaña al cliente durante la elección y confirmación. */
export function ResumenDeReserva({
  entidad,
  servicio,
  duracionMin,
  instanteIso,
  zona,
}: {
  entidad: string;
  servicio: string;
  duracionMin: number;
  instanteIso: string | null;
  zona: string;
}) {
  return (
    <aside className="resumen-lateral" aria-labelledby="titulo-resumen-reserva">
      <p className="etiqueta-resumen">{textos.resumenReserva.etiqueta}</p>
      <h3 id="titulo-resumen-reserva">{textos.resumenReserva.titulo}</h3>
      <dl>
        <div>
          <dt>{textos.resumenReserva.entidad}</dt>
          <dd>{entidad}</dd>
        </div>
        <div>
          <dt>{textos.resumenReserva.servicio}</dt>
          <dd>{servicio}</dd>
        </div>
        <div>
          <dt>{textos.resumenReserva.duracion}</dt>
          <dd>{textos.resumenReserva.minutos(duracionMin)}</dd>
        </div>
        <div>
          <dt>{textos.resumenReserva.cuando}</dt>
          <dd>
            {instanteIso === null
              ? textos.resumenReserva.porElegir
              : `${instanteLargo(instanteIso, zona)} · ${nombreDeZona(zona)}`}
          </dd>
        </div>
      </dl>
    </aside>
  );
}

/** Campo de formulario con su etiqueta, su pista y su mensaje de error asociados. */
export function Campo({
  id,
  etiqueta,
  pista,
  error,
  children,
}: {
  id: string;
  etiqueta: string;
  pista?: string;
  error?: string | null;
  children: (props: {
    id: string;
    'aria-describedby'?: string;
    'aria-invalid'?: boolean;
  }) => ReactNode;
}) {
  const idPista = pista === undefined ? undefined : `${id}-pista`;
  const idError = error === undefined || error === null ? undefined : `${id}-error`;
  const describedBy = [idPista, idError].filter((x) => x !== undefined).join(' ');

  return (
    <label className="campo" htmlFor={id}>
      <span>{etiqueta}</span>
      {pista !== undefined && (
        <span className="pista" id={idPista}>
          {pista}
        </span>
      )}
      {children({
        id,
        ...(describedBy === '' ? {} : { 'aria-describedby': describedBy }),
        ...(idError === undefined ? {} : { 'aria-invalid': true }),
      })}
      {idError !== undefined && (
        <span className="pista" id={idError} style={{ color: 'var(--error)' }} role="alert">
          {error}
        </span>
      )}
    </label>
  );
}

/** Flecha para volver al paso anterior, arriba a la izquierda de cada pantalla. */
export function BotonAtras({ alClick }: { alClick: () => void }) {
  return (
    <button
      type="button"
      className="boton-atras"
      aria-label={textos.aplicacion.volverAtras}
      title={textos.aplicacion.volverAtras}
      onClick={alClick}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 12H5m6-6-6 6 6 6" />
      </svg>
    </button>
  );
}
